import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import {
  CheckCircle2,
  Shield,
  TrendingUp,
  Users,
  Target,
  PiggyBank,
  LineChart,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ScrollReveal } from "./ScrollReveal";
import Header from "./Header";
import Footer from "./Footer";
import GoogleReviews from "./googleReviews";

const FinancialAdvisor = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    goal: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`/.netlify/functions/send-email`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        goal: "Financial Advisor",
        page_source: "financial-advisor",
        message: formData.goal.trim(),
        timestamp: new Date().toISOString(),
      });

      navigate(
        `/thank-you?service=financial-advisor&name=${encodeURIComponent(formData.name)}`,
      );
    } catch (error) {
      toast.error("Submission Failed", {
        description: "Please try again or call us directly at 918.477.7227",
      });
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f8fafb] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="hero-badge inline-flex items-center gap-2 px-4 py-2 bg-[#e8eef2] rounded-full text-[#3d4f5f] text-sm font-semibold mb-6">
                <Shield className="w-4 h-4" />
                Comprehensive Financial Planning for Oklahoma Families
              </div>
              <h1 className="hero-headline text-5xl sm:text-6xl font-bold text-[#3d4f5f] mb-6 leading-tight">
                Meet Curtis Wilson, Your Trusted Financial Advisor in Oklahoma
              </h1>
              <p className="hero-subheadline text-xl text-gray-600 mb-10">
                Financial guidance for investments, retirement, tax planning,
                and help you achieve your financial goals with confidence.
              </p>
              <div className="hero-cta flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  onClick={() =>
                    document
                      .getElementById("consultation-form")
                      .scrollIntoView({ behavior: "smooth" })
                  }
                  className="btn-primary btn-cta-pulse bg-[#3d4f5f] hover:bg-[#2d3e4e] text-white px-8 py-6 text-lg rounded-lg shadow-lg"
                >
                  Schedule a No-Cost Financial Consultation
                </Button>
                <Button
                  variant="outline"
                  className="sm:hidden border-2 border-[#3d4f5f] text-[#3d4f5f] px-8 py-6 text-lg rounded-lg hover:bg-[#f8fafb] transition-all duration-200"
                  onClick={() => (window.location.href = "tel:+19184777227")}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call a Local Advisor
                </Button>
              </div>
              <div className="hero-trust-badges flex flex-wrap justify-center lg:justify-start gap-8 mt-12 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274]" />
                  <span>Personalized Planning</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274]" />
                  <span>Financial Guidance</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274]" />
                  <span>Goal-Focused Approach</span>
                </div>
              </div>

              {/* <GoogleReviews />*/}
            </div>

            {/* Right Image */}
            <div className="hidden lg:block hero-cta">
              <img
                src="/financial-advisors.jpg"
                alt="Financial advisor meeting with client"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
                Comprehensive Financial Advisory Services
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                From investment management to estate planning strategies, we
                provide complete financial guidance
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <LineChart className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Investment Management
                </h3>
                <p className="text-gray-600">
                  Strategic portfolio management tailored to your risk tolerance
                  and financial objectives.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <Target className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Financial Planning
                </h3>
                <p className="text-gray-600">
                  Comprehensive plans covering budgeting, savings, debt
                  management, and goal achievement.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <PiggyBank className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Retirement Planning
                </h3>
                <p className="text-gray-600">
                  Strategic retirement planning helps ensure you can retire
                  comfortably on your terms.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <TrendingUp className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Tax Planning
                </h3>
                <p className="text-gray-600">
                  Tax-efficient strategies potentially minimize your tax burden
                  and maximize your wealth.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <Shield className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Risk Management
                </h3>
                <p className="text-gray-600">
                  Insurance and asset protection strategies designed to help
                  protect your family’s financial future.
                </p>
              </Card>
              <Card className="card-hover p-8 border-2 border-gray-100 hover:border-[#3d4f5f]">
                <Users className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Estate Planning Strategies
                </h3>
                <p className="text-gray-600">
                  Help ensure your legacy is protected and your wealth transfers
                  according to your wishes.
                </p>
              </Card>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Why Choose Us */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
                Why Oklahoma Families Choose Us
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="card-hover p-8 bg-white">
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Local Expertise
                </h3>
                <p className="text-gray-600">
                  Deep understanding of Oklahoma's economic landscape and
                  financial opportunities for local families.
                </p>
              </Card>
              <Card className="card-hover p-8 bg-white">
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Personalized Approach
                </h3>
                <p className="text-gray-600">
                  Your financial situation is unique. We create customized
                  strategies that align with your specific goals.
                </p>
              </Card>
              <Card className="card-hover p-8 bg-white">
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Long-Term Partnership
                </h3>
                <p className="text-gray-600">
                  We're committed to your success through every life stage, from
                  starting a family to retirement.
                </p>
              </Card>
              <Card className="card-hover p-8 bg-white">
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">
                  Transparent Communication
                </h3>
                <p className="text-gray-600">
                  Clear, jargon-free explanations so you always understand your
                  financial plan and investment strategy.
                </p>
              </Card>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Testimonials */}
      {/* <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
                What Our Clients Say
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="card-hover p-8 bg-[#f8fafb]">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src="https://ui-avatars.com/api/?name=Chris+N&rounded=true&bold=true&format=svg"
                    alt="Client testimonial"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#4a6274]" />
                      <span className="font-semibold text-[#3d4f5f]">
                        Chris N
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  "Over the last 35 years, Curt has demonstrated the flexibility
                  and dynamic knowledge of financial markets to turn what
                  appears to be a questionable situation into success at age 54.
                  I have doubts that I could have done this at all without his
                  help. I recommend him without reservation."
                </p>
              </Card>
              <Card className="card-hover p-8 bg-[#f8fafb]">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src="https://ui-avatars.com/api/?name=Pam+S&rounded=true&bold=true&format=svg"
                    alt="Client testimonial"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#4a6274]" />
                      <span className="font-semibold text-[#3d4f5f]">
                        Pam S
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  "Curt Wilson has been my financial advisor/planner for about
                  6.5 years. During that time I have seen the care he takes with
                  my investments and appreciate greatly the time he spends with
                  me explaining his thought processes and answering all my
                  questions. His education, professionalism and personal sense
                  of responsibility help me feel confident in all my dealings
                  with him."
                </p>
              </Card>
            </div>
          </div>
        </section>
      </ScrollReveal> */}

      {/* Consultation Form */}
      <section
        id="consultation-form"
        className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]"
      >
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#3d4f5f] mb-4">
              Start Your Financial Journey Today
            </h2>
            <p className="text-lg text-gray-600">
              Schedule a no-cost consultation with an Oklahoma financial advisor
            </p>
          </div>
          <Card className="p-8 shadow-xl border-2 border-gray-100 bg-white">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg"
                  placeholder="918.477.7227"
                />
              </div>
              <div>
                <label
                  htmlFor="goal"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Primary Financial Goal *
                </label>
                <Input
                  id="goal"
                  name="goal"
                  value={formData.goal}
                  onChange={handleChange}
                  required
                  className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg"
                  placeholder="e.g., Investment planning, Retirement savings, Tax strategy"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full bg-[#4a6274] hover:bg-[#3a5263] text-white py-6 text-lg font-semibold rounded-lg shadow-lg disabled:opacity-50"
              >
                {isSubmitting
                  ? "Submitting..."
                  : "Schedule a No-Cost Consultation"}
              </Button>
              <p className="text-sm text-gray-500 text-center">
                By submitting, you agree to be contacted about financial
                advisory services. No obligation.
              </p>
            </form>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FinancialAdvisor;
